import React from "react";
import { useSelector } from "react-redux";

import { DtGrid2 } from "../common/DtGrid2";
import { palstatDefaultColumnData } from "./PalstatDefaultColumnData";
import PalstatSearch from "./PalstatSearch";
import PalstatDefaultRightClick from "./PalstatDefaultRightClick";
import PalstatDefaultBottomButtons from "./PalstatDefaultBottomButtons";

import { onSetSelectedPallets, clearPalletCache, getPalletsRedux } from "../../redux/slices/palletCache.feature";
import { getPalstatActiveButtons } from "../../aplUtils/permissions";
import AplSettingsSelector from "../../redux/helpers/AplSettingsSelector";

import { useNavigate } from "react-router-dom";
import { DtGridPropsOptions2 } from "../../types/dtGridPropsOptions2";
import { IRootState } from "../../types/redux/IRootStore";
import { useAppDispatch } from "../../redux/hooks";
import { ModalType } from "../common/Modals/types/modalType";
import { copyPallets } from "../../aplUtils/clipboard";
import { getExceptionText, translateColumns } from "../../translations/gridTranslationHelper";
import { onSetFilters, onSetPage, onSetSort } from "../../redux/slices/palletCache.feature";
import { AplParameter } from "../../types/aplParameter";
import { parseBool } from "../../aplUtils/extensions";
import { closeModalRedux as closeModal, setModalMessage, showModalWithPalletsByObject } from "../../redux/slices/modalPallets2.feature";
import { AllowedPalletAction } from "../../types/allowedPalletAction";
import { PalletDetail } from "../../types/palletDetail";
import { bootboxAlert } from "../common/bootboxWrapper";
import { getAplTokens } from "../../aplUtils/cookiesWrapper";

const PalstatDefault = () => {
    const palletCacheRedux = useSelector((state: IRootState) => state.palletCache);
    const palletSearchRedux = useSelector((state: IRootState) => state.palletCache.palletSearch);
    const defectTypesRedux = useSelector((state: IRootState) => state.auth.defectTypes);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const aplSettings = AplSettingsSelector();
    const showBatchStr = aplSettings.siteParams.find(p => p.aplParameterType === AplParameter.ShowBatchInsteadOfLot);
    const showBatch = parseBool(showBatchStr?.value);
    let timeout = React.useRef<NodeJS.Timeout | null>(null);

    const onSetSelectedPalletsDispatcher = (x: any[]) => dispatch(onSetSelectedPallets(x));

    const dtGridOptns: DtGridPropsOptions2 = {
        data:
            (palletSearchRedux.qualitySwitch && palletCacheRedux.palletsBaseData
                ? palletCacheRedux.palletsBaseData.filter(x => x.wasBlocked)
                : palletCacheRedux.palletsBaseData) ?? [],
        columnData: translateColumns(
            palstatDefaultColumnData(aplSettings.site, aplSettings.culture, showBatch, palletSearchRedux.bulk, defectTypesRedux),
            aplSettings.culture
        ),
        defaultSort: {
            field: ["dtoPalletProductionDate"],
            direction: ["desc"]
        },
        selectType: aplSettings.siteId === 7 || palletSearchRedux.bulk ? "checkbox" : "single",
        colouring: true,
        headerFilters: true,
        setSelectedItems: onSetSelectedPalletsDispatcher,
        tableName: "tblPalstat",
        tableClass: "table-palstat",
        paging: true,
        showSpinner:
            palletCacheRedux.palletsBaseData.length === 0 && palletCacheRedux.palletsLoading && !palletCacheRedux.palletsFirstLoaded,
        rowId: "palletId",
        pageLength: 20,
        loading: palletCacheRedux.palletsLoading,
        bulk: palletSearchRedux.bulk,
        selectedItems: palletCacheRedux.selectedPallets,
        filters: palletSearchRedux.filters,
        setFilters: (filters: any) => dispatch(onSetFilters(filters)),
        page: palletSearchRedux.page,
        setPage: (page: number) => dispatch(onSetPage(page)),
        sort: palletSearchRedux.sort,
        setSort: (sort: any) => dispatch(onSetSort(sort)),
        selectors: palletSearchRedux.bulk
    };

    const handleBlockClick = () => {
        setModalVisibility(ModalType.Defects);
    };

    const setSelectedPallets = (pallets: PalletDetail[]) => {
        let newSelectionLength = pallets.length;
        let currentSelectionLength = palletCacheRedux.selectedPallets.length;

        if (newSelectionLength === 0) {
            bootboxAlert(`No pallets pallets with correct status selected`);
        } else if (newSelectionLength < currentSelectionLength) {
            dispatch(setModalMessage(`Processing ${newSelectionLength} out of ${currentSelectionLength} selected pallets`));
            dispatch(onSetSelectedPallets(pallets));
        }
    };

    const getOptions = () => {
        let opts = getPalstatActiveButtons(palletCacheRedux.selectedPallets, palletSearchRedux.bulk, aplSettings.isUsSite);

        return {
            ...opts,
            palletDetailsClick: () => {
                navigate(`/palstat/edit/${palletCacheRedux.selectedPallets[0].siteId}/${palletCacheRedux.selectedPallets[0].sscc}`);
            },
            blockClick: () => {
                let allowedPallets = palletCacheRedux.selectedPallets.filter(
                    x =>
                        x.allowedPalletActions.includes(AllowedPalletAction.BlockAny) ||
                        x.allowedPalletActions.includes(AllowedPalletAction.AddDefect)
                );
                setSelectedPallets(allowedPallets);
                setModalVisibilityWithPallets(ModalType.Defects, allowedPallets);
            },
            unblockClick: () => {
                let allowedPallets = palletCacheRedux.selectedPallets.filter(x =>
                    x.allowedPalletActions.includes(AllowedPalletAction.Unblock)
                );
                setSelectedPallets(allowedPallets);
                setModalVisibilityWithPallets(ModalType.Release, allowedPallets);
            },
            reprintClick: () => {
                let allowedPallets = palletCacheRedux.selectedPallets.filter(x =>
                    x.allowedPalletActions.includes(AllowedPalletAction.Reprint)
                );
                setSelectedPallets(allowedPallets);
                setModalVisibilityWithPallets(ModalType.Reprint, allowedPallets);
            },
            scrapClick: () => {
                let allowedPallets = palletCacheRedux.selectedPallets.filter(x =>
                    x.allowedPalletActions.includes(AllowedPalletAction.Scrap)
                );
                setSelectedPallets(allowedPallets);
                setModalVisibilityWithPallets(ModalType.Scrap, allowedPallets);
            },
            deleteClick: () => {
                let allowedPallets = palletCacheRedux.selectedPallets.filter(x =>
                    x.allowedPalletActions.includes(AllowedPalletAction.Delete)
                );
                setSelectedPallets(allowedPallets);
                setModalVisibilityWithPallets(ModalType.Delete, allowedPallets);
            },
            partPalletClick: () => {
                let allowedPallets = palletCacheRedux.selectedPallets.filter(x =>
                    x.allowedPalletActions.includes(AllowedPalletAction.Delete)
                );
                setSelectedPallets(allowedPallets);
                setModalVisibilityWithPallets(ModalType.PartPallet, allowedPallets);
            },
            partPallet: opts.delete && aplSettings.siteId === 9,
            copySsccClick: () => {
                copyPallets(palletCacheRedux.selectedPallets);
            },
            resetViewClick: () => {
                dispatch(clearPalletCache());
                handleSearch();
            },
            hideScrap: aplSettings.isUsSite,

            hidePartPallet: aplSettings.siteId !== 9
        };
    };
    const setModalVisibility = (modalType: ModalType) => {
        if (modalType === ModalType.None) {
            dispatch(closeModal());
        }
        dispatch(showModalWithPalletsByObject({ pallets: palletCacheRedux.selectedPallets, modalType: modalType }) as any);
    };

    const setModalVisibilityWithPallets = (modalType: ModalType, pallets: PalletDetail[]) => {
        if (modalType === ModalType.None) {
            dispatch(closeModal());
        }
        if (pallets.length > 0) dispatch(showModalWithPalletsByObject({ pallets: pallets, modalType: modalType }) as any);
    };

    const handleSearch = React.useCallback(() => {
        if(timeout.current) clearInterval(timeout.current);
        if (!getAplTokens().token) return;
        dispatch(getPalletsRedux(aplSettings.departmentId) as any);
        timeout.current = setTimeout(handleSearch, 30 * 1000);
    }, [dispatch, aplSettings.departmentId]);

    React.useEffect(() => {
        handleSearch();
        return () => {
            if(timeout.current) clearInterval(timeout.current);
        };
    }, [aplSettings.departmentId, dispatch, handleSearch, palletSearchRedux.bulk, palletSearchRedux.showAllPallets]);

    return (
        <React.Fragment>
            <div id="divContainer" className="container mw-100">
                <PalstatSearch handleBlockClick={handleBlockClick} handleSearch={handleSearch} />

                {palletCacheRedux.palletsLoadErrorMessage && !palletCacheRedux.palletsLoading ? (
                    <div id="div-error" className={`alert alert-danger ${palletCacheRedux.palletsLoadErrorMessage ? "" : "hide"}`} role="alert">
                        {getExceptionText(palletCacheRedux.palletsLoadErrorMessage, aplSettings.culture)}
                    </div>
                ) : (
                    <div id="div-error"></div>
                )}

                <DtGrid2 options={dtGridOptns}>
                    <PalstatDefaultRightClick options={getOptions()} />
                    <PalstatDefaultBottomButtons options={getOptions()} />
                </DtGrid2>
            </div>
        </React.Fragment>
    );
};

export default PalstatDefault;
